// NetFeed: the network implementation of the renderer's Feed interface (client/src/feed.ts), on top of TownNet.
// The renderer, HUD and screens read PublicView / PrivateView / SimEvent exactly as they do from LocalFeed; underneath,
// TownNet talks to the Colyseus room (or, when the server cannot be reached, runs the offline Table in the tab) and
// supplies interpolated bodies and a predicted own body.
//
//   const feed = await NetFeed.open(endpoint, { name, pid, cos, newcomer }, { kind: "public" });
//   if (!feed.online) …   // it fell back to the offline table (still a working Feed)
import type { Feed } from "../feed";
import type { PublicView, PrivateView, SimEvent, SeatInput, Vec3, Cosmetics, PublicBody } from "../../../shared/sim";
import { PRIVATE_EVENTS } from "../../../shared/protocol";
import { ROUND_SEC } from "../../../shared/tuning";
import type { Call } from "../../../shared/tuning";
import { TownNet } from "./index";
import type { Target, TownView, NetEvent, NetMode } from "./types";

export class NetFeed implements Feed {
  private v: TownView | null = null;
  private evs: SimEvent[] = []; private status: NetEvent[] = [];
  private lastPub: PublicView | null = null; private lastPriv: PrivateView | null = null;

  private constructor(readonly net: TownNet) {}

  /** connect (public quick-join, a code, or a new private table); a public join that cannot reach the server falls back
   *  to the offline table, a code that does not exist resolves with `error` set and mode "idle" */
  static async open(endpoint: string, info: { name: string; pid: string; cos: Cosmetics; newcomer: boolean }, target: Target = { kind: "public" }, opts: { solo?: boolean; plat?: string; build?: string } = {}) {
    const net = new TownNet({ endpoint, join: { name: info.name, pid: info.pid, cos: info.cos, first: info.newcomer, plat: opts.plat, build: opts.build }, solo: opts.solo });
    await net.start(target);
    return new NetFeed(net);
  }

  get online() { return this.net.mode === "online" || this.net.mode === "reconnecting"; }
  get mode(): NetMode { return this.net.mode; }
  get error() { return this.net.error; }

  update(_dt: number, nowMs: number): boolean {
    this.v = this.net.view(nowMs);
    for (const e of this.net.drainEvents()) {
      if (e.k === "net") { this.status.push(e); continue; }
      // the wire strips the routing field; everything private that reached us is ours
      this.evs.push((PRIVATE_EVENTS.has(e.k) ? { ...e, to: this.v.seat } : e) as SimEvent);
    }
    if (!this.v.ready || !this.v.head || !this.v.me) return false;
    this.lastPub = toPublic(this.v); this.lastPriv = toPrivate(this.v);
    return true;
  }
  pub(): PublicView { if (!this.lastPub) throw new Error("NetFeed.pub() before the first view (check update())"); return this.lastPub; }
  priv(): PrivateView { if (!this.lastPriv) throw new Error("NetFeed.priv() before the first view (check update())"); return this.lastPriv; }
  drain(): SimEvent[] { const e = this.evs; this.evs = []; return e; }
  /** connection changes (online / reconnecting / lost / offline / error) since the last call */
  drainStatus(): NetEvent[] { const e = this.status; this.status = []; return e; }
  input(i: SeatInput) { this.net.input(i); }
  pop(id: number, aim: Vec3 | null) { this.net.pop(id, aim ? [aim.x, aim.y, aim.z] : undefined); }
  mark(id: number, aim: Vec3) { this.net.mark(id, [aim.x, aim.y, aim.z]); }
  whistle(call?: Call) { this.net.whistle(call); }
  humans() { return this.v ? Math.max(1, this.v.roster.filter((r) => r.human).length) : 1; }
  roomCode() { return this.net.code; }
  leave() { this.net.leave(); }
}

function toPublic(v: TownView): PublicView {
  const h = v.head!;
  const bodies: PublicBody[] = v.bodies.map((b) => ({ id: b.id, x: b.x, z: b.z, h: b.h, anim: b.anim, age: b.age, costume: b.costume, blunder: b.blunder, glint: b.glint, revealed: b.revealed, inspector: b.inspector }));
  return {
    tick: h.tick, time: v.serverTime, phase: h.phase, round: h.round, phaseLeft: h.phaseLeft,
    playT: h.phase === "play" ? Math.max(0, ROUND_SEC - h.phaseLeft) : 0,
    vault: h.vault, goal: h.goal, parade: h.parade, onboarding: h.onboarding, winner: h.winner,
    call: h.call, whistle: h.whistle, stalls: h.stalls, golden: h.golden as PublicView["golden"], float: h.float, panics: h.panics, bus: h.bus,
    inspector: h.inspector, inspectorSeat: h.inspectorSeat, charges: h.charges, inspectorCos: h.inspectorCos,
    reveals: [...h.reveals].map(([id, c]) => ({ id, cos: { mask: c.mask, colour: c.colour, fx: c.fx, hat: 0, ray: 0 } })),
    seats: v.roster.map((r) => ({ name: r.name, human: r.human, role: r.inspector ? "inspector" : "sneak" })),
    nextInspectorSeat: h.nextInspectorSeat, bodies,
  };
}
function toPrivate(v: TownView): PrivateView {
  const m = v.me!, h = v.head!;
  const own = v.bodies.find((b) => b.me);
  return {
    seat: m.seat, role: m.role, me: m.me, x: own ? own.x : m.x, z: own ? own.z : m.z, h: own ? own.h : m.h, locked: m.locked,
    carry: m.carry as PrivateView["carry"], grab: m.grab, stash: m.stash, can: m.can, respawnIn: m.respawnIn,
    answered: m.answered as PrivateView["answered"], freezeHold: m.freezeHold,
    charges: m.charges, cooldown: m.cooldown, whistleIn: m.whistleIn, regenIn: m.regenIn, mark: m.mark,
    stashes: m.stashes, perfects: m.perfects, catches: m.catches, nextInspector: m.nextInspector,
    scriptGoal: null, gentle: m.role === "inspector" && h.onboarding,
  };
}
